<script lang="ts" setup>
import { useScrollSpy } from '#core/composables/useScrollSpy'
import { ConsultationSourceEnum } from '@forgd/contract/types'
import { Button } from '@forgd/ui-kit/button'
import { Card, CardContent } from '@forgd/ui-kit/card'
import { BanknotesIcon, ChartBarIcon } from '@heroicons/vue/24/outline'
import { createReusableTemplate } from '@vueuse/core'

useHead({
  title: 'Exchange Listing',
  htmlAttrs: {
    class: 'scroll-smooth',
  },
})

const route = useRoute()
const { destination: consultationDestination } = useConsultationDestination()
const { captureConsultationClick } = useConsultationTracking()

const basePath = computed(() => route.params.projectId ? `/project/${route.params.projectId}/list-on-exchanges` : '/list-on-exchanges')
const introPath = computed(() => basePath.value)
const ratingsPath = computed(() => `${basePath.value}/exchange-ratings`)
const feesPath = computed(() => `${basePath.value}/listing-fees`)

const ratingsChildSections = [
  { id: 'centralized-exchanges', label: 'CEX worth considering' },
  { id: 'price-performance', label: 'Price Performance' },
  { id: 'exchange-viability', label: 'Viability & Value' },
  { id: 'trading-kpis', label: 'Trading KPIs' },
  { id: 'cost-accessibility', label: 'Cost & Accessibility' },
  { id: 'exchange-profiles', label: 'Exchange Profiles' },
]

const feesChildSections = [
  { id: 'preferences-configuration', label: 'Preferences configuration' },
  { id: 'fees-breakdown', label: 'Fees Breakdown by Exchange' },
]

const isOnRatingsPage = computed(() => route.path === ratingsPath.value)
const isOnFeesPage = computed(() => route.path === feesPath.value)

// below md, CorePageHeaderMobile (56px) and CoreMobileNav (~45px) are stuck to the top of the viewport
const MOBILE_STICKY_HEADERS_PX = 100
const isMobile = useIsMobile()

const { activeSection, scrollToSection } = useScrollSpy(
  [{ id: 'exchange-rankings' }, ...ratingsChildSections, ...feesChildSections],
  { offsetPx: () => isMobile.value ? 24 + MOBILE_STICKY_HEADERS_PX : 24 },
)

function onRatingsChildClick(e: MouseEvent, sectionId: string) {
  if (isOnRatingsPage.value) {
    e.preventDefault()
    scrollToSection(sectionId)
  }
}

function onFeesChildClick(e: MouseEvent, sectionId: string) {
  if (isOnFeesPage.value) {
    e.preventDefault()
    scrollToSection(sectionId)
  }
}

const [DefineNavLinks, ReuseNavLinks] = createReusableTemplate()

const mobileNavLabel = computed(() => {
  if (isOnRatingsPage.value) {
    return ratingsChildSections.find(section => section.id === activeSection.value)?.label ?? 'Exchange Rankings'
  }
  if (isOnFeesPage.value) {
    return feesChildSections.find(section => section.id === activeSection.value)?.label ?? 'Exchanges Listing Fees'
  }
  return 'Introduction'
})
</script>

<template>
  <DefineNavLinks>
    <NuxtLink
      :to="introPath"
      class="flex h-8.5 w-full items-center rounded-sm p-2 hover:bg-neutral-400"
      :class="[route.path === introPath ? 'bg-neutral-400' : '']"
    >
      <span class="text-xs font-normal whitespace-nowrap text-gray-600">
        Introduction
      </span>
    </NuxtLink>

    <div class="my-1 border-b border-default" />

    <div class="flex h-8.5 w-full items-center gap-2 p-2">
      <ChartBarIcon class="size-3 shrink-0 text-placeholder" aria-hidden="true" />
      <span class="font-regular text-xs leading-none whitespace-nowrap text-placeholder">
        Exchange Rankings
      </span>
    </div>

    <NuxtLink
      v-for="section in ratingsChildSections"
      :key="section.id"
      :to="`${ratingsPath}#${section.id}`"
      class="flex h-8.5 w-full items-center rounded-sm p-2 text-left hover:bg-neutral-400"
      :class="[isOnRatingsPage && activeSection === section.id ? 'bg-neutral-400' : '']"
      @click="onRatingsChildClick($event, section.id)"
    >
      <span class="font-regular text-xs leading-none whitespace-nowrap text-secondary">
        {{ section.label }}
      </span>
    </NuxtLink>

    <div class="my-1 border-b border-default" />

    <div class="flex h-8.5 w-full items-center gap-2 p-2">
      <BanknotesIcon class="size-3 shrink-0 text-placeholder" aria-hidden="true" />
      <span class="font-regular text-xs leading-none whitespace-nowrap text-placeholder">
        Exchanges Listing Fees
      </span>
    </div>

    <NuxtLink
      v-for="section in feesChildSections"
      :key="section.id"
      :to="`${feesPath}#${section.id}`"
      class="flex h-8.5 w-full items-center rounded-sm p-2 text-left hover:bg-neutral-400"
      :class="[isOnFeesPage && activeSection === section.id ? 'bg-neutral-400' : '']"
      @click="onFeesChildClick($event, section.id)"
    >
      <span class="font-regular text-xs leading-none whitespace-nowrap text-secondary">
        {{ section.label }}
      </span>
    </NuxtLink>

    <div class="my-2 border-b border-default" />
    <Card>
      <CardContent class="flex flex-col gap-5 p-4">
        <div class="flex flex-col gap-2">
          <img
            src="/icons/exchange-listing/consultation.svg"
            alt="Schedule a Consultation"
            class="size-12"
          >
          <h4 class="text-xs font-semibold text-primal">
            Get advisory for your listing from Forgd.
          </h4>
          <p class="text-xs text-secondary">
            We connect you directly with centralized exchanges to list your token.
          </p>
        </div>
        <Button as-child size="sm" block>
          <NuxtLink
            :to="consultationDestination.to"
            :external="consultationDestination.external"
            :target="consultationDestination.target"
            @click="captureConsultationClick(ConsultationSourceEnum.ExchangeListingSidebar, consultationDestination.to)"
          >
            Book a Free Consultation
          </NuxtLink>
        </Button>
      </CardContent>
    </Card>
  </DefineNavLinks>

  <NuxtLayout
    name="dashboard"
    supports-mobile
  >
    <template #header>
      <div class="flex items-center gap-2">
        <div>
          <img
            :src="String('/icons/exchange-listing-research.svg')"
            width="32"
            height="32"
            alt="List on Exchanges"
          >
        </div>
        <div class="flex shrink-0 flex-col justify-center">
          <span class="text-xs leading-none font-normal text-secondary">
            Free Tools
          </span>
          <span class="text-sm font-semibold text-primal">
            List on Exchanges
          </span>
        </div>
      </div>
    </template>

    <template #sidebar>
      <div class="h-full w-sidebar border-r border-default max-md:hidden">
        <nav
          aria-label="Exchange Listing Navigation"
          class="sticky top-0 flex shrink-0 flex-col gap-1 px-2 py-4"
        >
          <ReuseNavLinks />
        </nav>
      </div>
    </template>

    <template #subheader>
      <CoreMobileNav :active-label="mobileNavLabel" label="List on Exchanges" lock-scroll>
        <nav aria-label="Exchange Listing Navigation" class="flex flex-col gap-1">
          <ReuseNavLinks />
        </nav>
      </CoreMobileNav>
    </template>

    <div class="@container mx-auto mb-5 w-full max-w-280 min-w-0">
      <slot />
    </div>

    <template #app-footer>
      <CoreAppFooterBlock />
    </template>
  </NuxtLayout>
</template>
